import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  getGuideIndex,
  getGuideContent,
  type GuideIndex,
  type GuideContentEnhanced,
} from '../lib/api'
import type { TabId } from '../components/help-widget/WidgetTabBar'

/**
 * Map route prefixes to guide section slugs.
 * Ordered by specificity (longest prefix first) so matching works correctly.
 */
const ROUTE_TO_GUIDE: [string, string][] = [
  ['/workspace', 'workspaces'],
  ['/apps/', 'apps'],
  ['/apps', 'apps'],
  ['/browse', 'apps'],
  ['/settings', 'getting-started'],
  ['/changelog', 'getting-started'],
  ['/guide', 'getting-started'],
  ['/admin', 'getting-started'],
  ['/', 'getting-started'],
]

function resolveSection(pathname: string): string {
  for (const [prefix, section] of ROUTE_TO_GUIDE) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return section
    }
  }
  return 'getting-started'
}

/**
 * Overlay screens that float above the tab content.
 * These are independent of which tab is active.
 */
export interface FeedbackPrefill {
  title?: string
  description?: string
}

export type OverlayScreen =
  | null
  | { screen: 'feedback-type' }
  | { screen: 'feedback-form'; feedbackType: string; prefill?: FeedbackPrefill }

export function useHelpWidget() {
  const { pathname } = useLocation()

  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [activeTab, setActiveTab] = useState<TabId>('home')
  const [overlay, setOverlay] = useState<OverlayScreen>(null)
  const [guideIndex, setGuideIndex] = useState<GuideIndex | null>(null)
  const [indexLoading, setIndexLoading] = useState(false)
  const [currentSection, setCurrentSection] = useState('')
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null)
  const [sectionContent, setSectionContent] = useState<GuideContentEnhanced | null>(null)
  const [loading, setLoading] = useState(false)

  // Listen for programmatic widget open events (from HelpLink component)
  useEffect(() => {
    const handler = (e: Event) => {
      const { section } = (e as CustomEvent).detail
      setCurrentSection(section)
      setActiveTab('help')
      setIsExpanded(true)
      setOverlay(null)
      setIsOpen(true)
    }
    window.addEventListener('help:open', handler)
    return () => window.removeEventListener('help:open', handler)
  }, [])

  // Listen for "open chat" events (e.g. from the sidebar AI Chatbot item)
  useEffect(() => {
    const handler = () => {
      setActiveTab('chat')
      setIsExpanded(false)
      setOverlay(null)
      setIsOpen(true)
    }
    window.addEventListener('help:open-chat', handler)
    return () => window.removeEventListener('help:open-chat', handler)
  }, [])

  const openWidget = useCallback(() => {
    setActiveTab('home')
    setIsExpanded(false)
    setOverlay(null)
    setIsOpen(true)
  }, [])

  const closeWidget = useCallback(() => {
    setIsOpen(false)
    setIsExpanded(false)
    setActiveTab('home')
    setOverlay(null)
  }, [])

  const switchTab = useCallback((tab: TabId) => {
    setActiveTab(tab)
    setOverlay(null)
    // Collapse when switching tabs (expanded only for guide reading)
    setIsExpanded(false)
    // When switching to help tab, auto-resolve the section from current route
    if (tab === 'help' && !currentSection) {
      setCurrentSection(resolveSection(pathname))
    }
  }, [currentSection, pathname])

  const goHomeTab = useCallback(() => {
    setActiveTab('home')
    setIsExpanded(false)
    setOverlay(null)
  }, [])

  /** Navigate to a guide section — expands the widget and switches to help tab */
  const goGuide = useCallback((section?: string, anchor?: string) => {
    const resolved = section || resolveSection(pathname)
    setCurrentSection(resolved)
    setPendingAnchor(anchor || null)
    setActiveTab('help')
    setIsExpanded(true)
    setOverlay(null)
  }, [pathname])

  /** Collapse expanded view back to section list */
  const collapseGuide = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const goFeedbackType = useCallback(() => {
    setOverlay({ screen: 'feedback-type' })
  }, [])

  const goFeedbackForm = useCallback((feedbackType: string, prefill?: FeedbackPrefill) => {
    setOverlay({ screen: 'feedback-form', feedbackType, prefill })
  }, [])

  const dismissOverlay = useCallback(() => {
    setOverlay(null)
  }, [])

  // Fetch guide index on first open
  useEffect(() => {
    if (!isOpen || guideIndex) return

    let cancelled = false
    setIndexLoading(true)

    getGuideIndex()
      .then((data) => {
        if (!cancelled) {
          setGuideIndex(data)
        }
      })
      .catch(() => {
        // Silently fail - guide index is non-critical
      })
      .finally(() => {
        if (!cancelled) {
          setIndexLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, guideIndex])

  // Fetch section content when currentSection changes
  useEffect(() => {
    if (!currentSection) return

    let cancelled = false
    setLoading(true)
    setSectionContent(null)

    getGuideContent(currentSection)
      .then((data) => {
        if (!cancelled) {
          setSectionContent(data)
        }
      })
      .catch(() => {
        // Silently fail
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [currentSection])

  return {
    isOpen,
    isExpanded,
    openWidget,
    closeWidget,
    activeTab,
    switchTab,
    overlay,
    goHomeTab,
    goGuide,
    collapseGuide,
    guideIndex,
    currentSection,
    setCurrentSection,
    pendingAnchor,
    setPendingAnchor,
    sectionContent,
    loading,
    indexLoading,
    goFeedbackType,
    goFeedbackForm,
    dismissOverlay,
    pathname,
  }
}
